import React, { useMemo, useCallback, useState, useEffect } from 'react'
import {
  TextField,
  InputAdornment,
  Button,
  useMediaQuery,
  useTheme
} from '@material-ui/core'
import { Search as SearchIcon, Cancel as CancelIcon } from '@material-ui/icons'
import { useFormik } from 'formik'
import { useRouter } from 'next/router'
import isEmpty from 'lodash-es/isEmpty'
import debounce from 'lodash-es/debounce'

import { useFilterSearchBarStyles } from './filter-search-bar.styles'
import { SEARCH_QUERY_KEY, useQueryPrivateRequests } from '../filters'
import { useTranslation } from '@plandi/common'

type FilterSearchBarProps = {
  disabled?: boolean
  typeForSearch?: string
}

export const FilterSearchBar: React.FC<FilterSearchBarProps> = (props) => {
  const [keyword, setKeyword] = useState<string | undefined>('')
  const { data: privateRequests } = useQueryPrivateRequests(
    keyword,
    props.typeForSearch
  )

  const router = useRouter()
  const queryKeyword = router.query.keyword as string | undefined

  const classes = useFilterSearchBarStyles()

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down(960))
  const { data: translation } = useTranslation()
  useEffect(() => {
    setKeyword(queryKeyword)
  }, [queryKeyword])

  const onPushQueryKeyword = useCallback(
    (value: string) => {
      router.push(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            [SEARCH_QUERY_KEY]: value,
            page: 1
          }
        },
        undefined,
        {
          shallow: true
        }
      )
    },
    [router]
  )

  const formik = useFormik({
    initialValues: { keyword: queryKeyword ?? '' },
    enableReinitialize: true,
    onSubmit: (value) => onPushQueryKeyword(value.keyword)
  })

  const handleClear = () => {
    formik.setFieldValue(SEARCH_QUERY_KEY, '')
    setKeyword('')
    onPushQueryKeyword('')
  }

  const onChangeInput = useMemo(
    () =>
      debounce((event) => {
        if (event.target.value && event.target.value.length >= 4) {
          setKeyword(event.target.value)
        } else {
          setKeyword('')
        }
      }, 800),
    []
  )

  return (
    <form onSubmit={formik.handleSubmit} className={classes.root}>
      <TextField
        value={formik.values.keyword}
        onChange={(event) => {
          formik.setFieldValue(SEARCH_QUERY_KEY, event.target.value)
          onChangeInput(event)
        }}
        name={SEARCH_QUERY_KEY}
        placeholder={translation?.search}
        className={classes.input}
        variant="standard"
        disabled={props.disabled}
        InputProps={{
          disableUnderline: true,
          style: {
            height: !isMobile ? '50px' : '38px'
          },
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon color={!isMobile ? 'action' : 'primary'} />
            </InputAdornment>
          ),
          endAdornment: formik.values.keyword.length > 0 && (
            <InputAdornment
              position="start"
              onClick={handleClear}
              className={classes.action}
            >
              <CancelIcon color="action" />
            </InputAdornment>
          )
        }}
      />
      {!isMobile && (
        <Button
          type="submit"
          variant="contained"
          color="primary"
          className={classes.button}
          disabled={props.disabled}
        >
          {translation?.search}
        </Button>
      )}
      {props.disabled ? (
        <div className={classes.hidden}></div>
      ) : (
        !isEmpty(props.typeForSearch) && (
          <div className={classes.privateRequest}>
            {!isEmpty(privateRequests?.data) &&
              privateRequests?.data.map((item) => (
                <Button
                  key={item.id}
                  type="submit"
                  className={classes.privateRequestButton}
                  onClick={() => {
                    setKeyword(item.search)
                    onPushQueryKeyword(item.search)
                  }}
                >
                  {item.search}
                </Button>
              ))}
          </div>
        )
      )}
    </form>
  )
}
